import { Component, effect, inject, signal, untracked } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { ImpersonationStore, LogService } from 'shared';

import { AuthStore } from './features/authentication/store/auth.store';
import { EstablishmentStore } from './features/establishment';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent {
  title = 'link-generator';

  private router = inject(Router);
  private authStore = inject(AuthStore);
  private establishmentStore = inject(EstablishmentStore);
  private impersonation = inject(ImpersonationStore);
  private logService = inject(LogService);
  private hasAttemptedLoad = signal(false);

  constructor() {
    // Effect to check email verification (skip during impersonation)
    effect(() => {
      const isAuthenticated = this.authStore.isAuthenticated();
      const user = this.authStore.user();
      const isImpersonating = this.impersonation.isImpersonating();

      // Skip email verification check during impersonation
      if (isImpersonating) {
        return;
      }

      if (isAuthenticated && user && user.isEmailConfirmed === false) {
        const currentUrl = untracked(() => this.router.url);
        // Don't redirect if already on auth pages
        if (!currentUrl.startsWith('/auth')) {
          void this.router.navigate(['/auth/registration-confirmation']);
        }
      }
    });

    // Effect to load establishments when authenticated and email is verified (or impersonating)
    effect(() => {
      const isAuthenticated = this.authStore.isAuthenticated();
      const user = this.authStore.user();
      const isEmailConfirmed = user?.isEmailConfirmed ?? false;
      const isImpersonating = this.impersonation.isImpersonating();

      // Use untracked to avoid re-running effect when these change
      const hasEstablishments = untracked(
        () => this.establishmentStore.establishments().length > 0,
      );
      const isLoadingEstablishments = untracked(() => this.establishmentStore.isLoading());
      const alreadyAttempted = untracked(() => this.hasAttemptedLoad());

      // Load establishments if email is confirmed OR if impersonating (skip email check during impersonation)
      const canLoadEstablishments = isEmailConfirmed || isImpersonating;

      if (
        isAuthenticated &&
        canLoadEstablishments &&
        !hasEstablishments &&
        !isLoadingEstablishments &&
        !alreadyAttempted
      ) {
        this.hasAttemptedLoad.set(true);
        this.establishmentStore.loadEstablishments().subscribe();
      }

      // Reset flag when user logs out
      if (!isAuthenticated) {
        this.hasAttemptedLoad.set(false);
      }
    });

    // Effect to redirect to onboarding if no establishments after loading
    effect(() => {
      const isAuthenticated = this.authStore.isAuthenticated();
      const user = this.authStore.user();
      const isEmailConfirmed = user?.isEmailConfirmed ?? false;
      const establishments = this.establishmentStore.establishments();
      const isLoading = this.establishmentStore.isLoading();
      const hasAttempted = this.hasAttemptedLoad();
      const isImpersonating = this.impersonation.isImpersonating();

      this.logService.debug('AppComponent', '[AppComponent] Onboarding check:', {
        isAuthenticated,
        isEmailConfirmed,
        hasAttempted,
        isLoading,
        establishmentsCount: establishments.length,
        isImpersonating,
        impersonatedUser: this.impersonation.impersonatedUser(),
      });

      // Skip email confirmation check during impersonation
      const canCheckOnboarding = isEmailConfirmed || isImpersonating;

      // Redirect to onboarding if no establishments (after email is confirmed OR during impersonation)
      if (
        isAuthenticated &&
        canCheckOnboarding &&
        hasAttempted &&
        !isLoading &&
        establishments.length === 0
      ) {
        const currentUrl = this.router.url;
        // Don't redirect if already on onboarding or auth pages
        if (!currentUrl.startsWith('/onboarding') && !currentUrl.startsWith('/auth')) {
          this.logService.debug(
            'AppComponent',
            '[AppComponent] Redirecting to onboarding - no establishments found',
          );
          void this.router.navigate(['/onboarding/setup']);
        }
      }
    });
  }

  // Note: sessionStorage automatically clears when the browser tab is closed.
  // We previously had a beforeunload handler here, but it was clearing storage
  // on page reload too (breaking impersonation flow). Removed because sessionStorage
  // handles tab-close cleanup automatically.
}
